import { Helmet } from "react-helmet";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { CheckCircle, Users, TrendingUp, Shield, ArrowRight, Phone, Mail } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const Membership = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Become a Member | Natcon Sacco</title>
        <meta 
          name="description" 
          content="Ready to join? Learn about the simple 3-step process, membership benefits, and requirements to become a member of Natcon Sacco today. Apply online!" 
        />
        <meta name="keywords" content="Join Natcon Sacco, how to join a Sacco in Kenya, NCA Sacco, Sacco membership requirements, start saving Kenya" />
        <link rel="canonical" href="/membership" />
      </Helmet>

      <Header />
      
      <main className="pt-20">
        {/* Part 1: Page Header and Compelling Introduction */}
        <section className="py-16 bg-gradient-to-br from-primary/5 to-secondary/5">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl 2xl:text-5xl font-bold text-foreground mb-6 leading-tight">
                Join Natcon Sacco: Grow With Our Community
              </h1>
              <div className="text-lg text-muted-foreground leading-relaxed space-y-4">
                <p>
                  Become a part of a trusted and growing financial family. Since 2014, Natcon Sacco has been dedicated to the financial well-being of our members. We've grown from an ambitious group of 30 to a strong community of over 259 members, all committed to mutual growth and prosperity.
                </p>
                <p>
                  With a member retention rate of <span className="text-primary font-semibold">95%</span>, we are a Sacco that people join and stay with. Here's how you can start your journey with us.
                </p>
              </div>
              <div className="flex flex-wrap justify-center gap-4 mt-8">
                <Badge variant="secondary" className="text-sm px-4 py-2">
                  <Users className="w-4 h-4 mr-2" />
                  259+ Members
                </Badge>
                <Badge variant="secondary" className="text-sm px-4 py-2">
                  <TrendingUp className="w-4 h-4 mr-2" />
                  95% Retention Rate
                </Badge>
                <Badge variant="secondary" className="text-sm px-4 py-2">
                  <Shield className="w-4 h-4 mr-2" />
                  Since 2014
                </Badge>
              </div>
            </div>
          </div>
        </section>

        {/* Part 2: Your Simple, 3-Step Path to Membership */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-foreground mb-12">
                Your Path to Joining Us
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {/* Step 1 */}
                <div className="text-center relative">
                  <div className="bg-primary text-primary-foreground rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                    1
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Confirm Your Eligibility</h3>
                  <p className="text-muted-foreground">
                    Membership is currently open to all employees of the National Construction Authority (NCA). We are actively working on plans to expand our common bond to welcome members from other public sector organizations in the near future.
                  </p>
                  {/* Connector line for desktop */}
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-border -translate-x-1/2 -z-10"></div>
                </div>

                {/* Step 2 */}
                <div className="text-center relative">
                  <div className="bg-primary text-primary-foreground rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                    2
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Your Ownership Stake</h3>
                  <p className="text-muted-foreground mb-4">
                    Becoming a member means becoming an owner. Your initial investment secures your stake in the Sacco and is composed of two parts:
                  </p>
                  <div className="space-y-3">
                    <div className="bg-card border rounded-lg p-4">
                      <h4 className="font-medium text-foreground">One-Time Joining Fee</h4>
                      <p className="text-sm text-muted-foreground">A non-refundable fee of <span className="font-semibold text-primary">Kshs. 1,500</span> to process your membership.</p>
                    </div>
                    <div className="bg-card border rounded-lg p-4">
                      <h4 className="font-medium text-foreground">Minimum Share Capital</h4>
                      <p className="text-sm text-muted-foreground">A one-time purchase of <span className="font-semibold text-primary">Kshs. 5,000</span> (250 shares at Kshs. 20 each). This is your ownership stake in the Sacco and earns you dividends.</p>
                    </div>
                  </div>
                  {/* Connector line for desktop */}
                  <div className="hidden md:block absolute top-8 left-full w-full h-0.5 bg-border -translate-x-1/2 -z-10"></div>
                </div>

                {/* Step 3 */}
                <div className="text-center">
                  <div className="bg-primary text-primary-foreground rounded-full w-16 h-16 flex items-center justify-center text-2xl font-bold mx-auto mb-6">
                    3
                  </div>
                  <h3 className="text-xl font-semibold mb-4">Complete Your Application</h3>
                  <p className="text-muted-foreground mb-6">
                    Ready to start? Our online application process is quick, easy, and secure. Click the button below to begin.
                  </p>
                  <Button size="lg" className="w-full" asChild>
                    <a href="/membership-application">
                      Apply Online Now
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Part 3: The Benefits of Being a Natcon Sacco Member */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-foreground mb-12">
                Why Join Us? The Natcon Advantage
              </h2>
              
              <div className="grid md:grid-cols-3 gap-8">
                {/* Card 1 */}
                <Card className="h-full">
                  <CardHeader className="text-center">
                    <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <TrendingUp className="w-8 h-8 text-primary" />
                    </div>
                    <CardTitle className="text-xl">Build Your Savings & Earn Returns</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-center">
                      Grow your wealth through consistent monthly deposits. As a member, you earn competitive interest on your deposits and annual dividends on your share capital, ensuring your money works for you.
                    </CardDescription>
                  </CardContent>
                </Card>

                {/* Card 2 */}
                <Card className="h-full">
                  <CardHeader className="text-center">
                    <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-primary" />
                    </div>
                    <CardTitle className="text-xl">Access Loans to Achieve Your Goals</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-center">
                      After just six months of membership, you become eligible for our range of affordable loan products. Are you transferring from another Sacco? You qualify for a loan immediately! Take advantage of our Emergency, Development, and School Fees loans.
                    </CardDescription>
                  </CardContent>
                </Card>

                {/* Card 3 */}
                <Card className="h-full">
                  <CardHeader className="text-center">
                    <div className="bg-primary/10 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-primary" />
                    </div>
                    <CardTitle className="text-xl">Join a Strong & Supportive Community</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-center">
                      You're not just a customer; you're a member-owner. We pride ourselves on our strong sense of community, member-focused service, and transparent governance led by our dedicated Board of Directors.
                    </CardDescription>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Part 4: Mini FAQ for This Page */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-foreground mb-12">
                Quick Answers
              </h2>
              
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="item-1">
                  <AccordionTrigger className="text-left">
                    What is the difference between my Deposits and my Share Capital?
                  </AccordionTrigger>
                  <AccordionContent>
                    Your Deposits are your non-withdrawable savings which earn you interest and determine your loan eligibility. Your Share Capital is your non-refundable ownership stake in the Sacco which earns you dividends.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-2">
                  <AccordionTrigger className="text-left">
                    How soon can I take a loan after joining?
                  </AccordionTrigger>
                  <AccordionContent>
                    You are eligible for a loan after six months. However, if you are transferring your membership from another Sacco, you qualify immediately.
                  </AccordionContent>
                </AccordionItem>
                
                <AccordionItem value="item-3">
                  <AccordionTrigger className="text-left">
                    Who runs the Sacco?
                  </AccordionTrigger>
                  <AccordionContent>
                    The Sacco is managed by a democratically elected Board of Directors and a Supervisory Committee who are accountable to the members.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </section>

        {/* Part 5: Final Call-to-Action */}
        <section className="py-16 bg-gradient-to-r from-primary to-primary/80">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-primary-foreground mb-6">
                Your Journey to Financial Empowerment Starts Here
              </h2>
              <div className="space-y-6">
                <Button size="lg" variant="secondary" className="text-lg px-8 py-3" asChild>
                  <a href="/membership-application">
                    Start My Application
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </a>
                </Button>
                <div className="text-primary-foreground/80">
                  <p className="mb-2">Have More Questions?</p>
                  <div className="flex flex-wrap justify-center gap-6 text-sm">
                    <a href="/contact" className="hover:text-primary-foreground transition-colors flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Contact Our Support Team
                    </a>
                    <a href="mailto:natconsacco@nca.go.ke" className="hover:text-primary-foreground transition-colors flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      natconsacco@nca.go.ke
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Membership;